import React, { useEffect, useState } from "react";
import { useScannerContext } from "../scanners/ScannerContext";
import { QualityCheckDto, QualityCheckAction, BarcodeDataType, ScanActionApiResponseNew } from "../scanners/types";
import { apiHost, apiKey, showManualQrCodeInput } from "@/constants";
import { appLog, barcodeDataTypeFromQrCode, hasQRcodeNumberPattern } from "../scanners/helpers";
import { BagCard } from "@/components/ScannerActions/BagCard";
import QrCodeInputField from "../scanners/QrCodeInputField";
import { Order, OrderBag, Bin } from "@/types";
import { BinCard } from "@/components/ScannerActions/BinCard";

const messages = {
  scan_bin_qrcode_message: "Scan eerst een bak barcode",
  invalid_qrcode_format_message: "Geen geldig formaat voor QR code van bak of zak",
  invalid_qrcode_format_log: "[SCANNER_DATA] not a QRcode Number Pattern",
};

export const Scan: React.FC = () => {
  const { scannedData, setScannedData, enableScanner, clearScannedData } = useScannerContext();

  const [binDataString, setBinDataString] = useState<string | null>(null);
  const [bagDataString, setBagDataString] = useState<string | null>(null);

  const [order, setOrder] = useState<Partial<Order> | null>(null);
  const [bin, setBin] = useState<Partial<Bin> | null>(null);
  const [orderBag, setOrderBag] = useState<Partial<OrderBag> | null>(null);

  const [qualityCheckResponse, setQualityCheckResponse] = useState<ScanActionApiResponseNew | null>(null);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    return () => {
      clearScannedData();
    };
  }, [clearScannedData]);

  useEffect(() => {
    if (scannedData) {
      if (hasQRcodeNumberPattern(scannedData)) {
        // Get the entity the barcode belongs to (product, bag, bin)
        const dataLabelType = barcodeDataTypeFromQrCode(scannedData);
        // Bin scan
        if (dataLabelType === BarcodeDataType.ORDER_BIN) {
          setBinDataString(scannedData);
          setBagDataString(null);
          // Product scan
        } else if (dataLabelType === BarcodeDataType.ORDER_BAG) {
          if (binDataString) {
            setBagDataString(scannedData);
          } else {
            setMessage(messages.scan_bin_qrcode_message);
          }
        } else {
          setMessage(messages.invalid_qrcode_format_message);
          appLog(messages.invalid_qrcode_format_log, true, "info", { datalabel: scannedData });
        }
      } else {
        setMessage(messages.invalid_qrcode_format_message);
        appLog(messages.invalid_qrcode_format_log, true, "info", { datalabel: scannedData });
      }
    }
  }, [scannedData, binDataString]);

  useEffect(() => {
    setMessage("");
    if (binDataString) {
      fetchApiData(binDataString, bagDataString);
    }
  }, [binDataString, bagDataString]);

  const fetchApiData = async (binDataString: string | null, bagDataString: string | null) => {
    try {
      const data: QualityCheckDto = {
        action: QualityCheckAction.BIN_BAGS,
        dataStrings: [],
      };
      // Add qr-code dataStrings to the request body
      if (binDataString) {
        data.dataStrings.push({ type: BarcodeDataType.ORDER_BIN, dataString: binDataString });
        if (bagDataString) {
          data.dataStrings.push({ type: BarcodeDataType.ORDER_BAG, dataString: bagDataString });
        }
      } else {
        return;
      }

      const response = await fetch(`${apiHost}/order-quality-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify(data),
      });
      const responseData: ScanActionApiResponseNew = await response.json();
      setQualityCheckResponse(responseData || null);
      if (responseData.order) {
        setOrder(responseData.order);
      }
      if (responseData.bin) {
        setBin(responseData.bin);
      }
      if (responseData.orderBag) {
        setOrderBag(responseData.orderBag);
      }
    } catch (err) {
      appLog((err as Error).message, true, "error", err as Error);
      setOrder(null);
      setBin(null);
      setOrderBag(null);
    }
  };

  useEffect(() => {
    void enableScanner(true);
  }, [enableScanner]);

  return (
    <>
      <BinCard data={qualityCheckResponse} bin={bin} order={order} />
      <BagCard data={qualityCheckResponse} orderBag={orderBag} />
      {showManualQrCodeInput && (
        <QrCodeInputField
          onChange={(value: string) => {
            setScannedData(value);
          }}
        />
      )}
      {message && <div>{message}</div>}
    </>
  );
};

export default Scan;
